import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { FiPlus, FiEdit2, FiTrash2, FiShield } from 'react-icons/fi';
import {
  fetchIpWhitelist,
  createIpWhitelist,
  updateIpWhitelist,
  deleteIpWhitelist
} from '../redux/slices/ipWhitelistSlice';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Table from '../components/common/Table';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import Modal from '../components/common/Modal';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Pagination from '../components/common/Pagination';
import projectService from '../services/projectService';
import { toast } from 'react-toastify';
import './IpWhitelist.css';

const initialFormState = {
  project_id: '',
  ip_address: '',
  description: ''
};

const IpWhitelist = () => {
  const dispatch = useDispatch();
  const { ipList, pagination, loading } = useSelector((state) => state.ipWhitelist);

  const [projects, setProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [formData, setFormData] = useState(initialFormState);
  const [selectedIp, setSelectedIp] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [projectFilter, setProjectFilter] = useState('');

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const response = await projectService.getProjects();
        setProjects(response.data || []);
      } catch (error) {
        console.error('Failed to load projects:', error);
      }
    };
    loadProjects();
  }, []);

  useEffect(() => {
    dispatch(fetchIpWhitelist({ project_id: projectFilter || undefined }));
  }, [dispatch, projectFilter]);

  const handleOpenCreate = () => {
    setFormData(initialFormState);
    setModalMode('create');
    setShowModal(true);
  };

  const handleOpenEdit = (ip) => {
    setFormData({
      description: ip.description || '',
      is_active: ip.is_active
    });
    setSelectedIp(ip);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (modalMode === 'create') {
        await dispatch(createIpWhitelist(formData)).unwrap();
        toast.success('IP address whitelisted successfully');
      } else {
        await dispatch(
          updateIpWhitelist({ id: selectedIp.id, data: formData })
        ).unwrap();
        toast.success('IP entry updated successfully');
      }
      setShowModal(false);
      setFormData(initialFormState);
    } catch (error) {
      toast.error(error);
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteIpWhitelist(selectedIp.id)).unwrap();
      toast.success('IP removed from whitelist');
      setShowDeleteConfirm(false);
      setSelectedIp(null);
    } catch (error) {
      toast.error(error);
    }
  };

  const projectOptions = [
    { value: '', label: 'Select Project' },
    ...projects.map(p => ({ value: p.id.toString(), label: p.display_name }))
  ];

  const columns = [
    {
      key: 'ip_address',
      title: 'IP Address',
      render: (value) => (
        <div className="ip-cell">
          <FiShield className="ip-icon" />
          <span className="ip-address">{value}</span>
        </div>
      )
    },
    {
      key: 'project',
      title: 'Project',
      render: (_, row) => <Badge variant="default">{row.project?.display_name || row.project?.name || '-'}</Badge>
    },
    {
      key: 'description',
      title: 'Description',
      render: (value) => <span className="ip-description">{value || '-'}</span>
    },
    {
      key: 'is_active',
      title: 'Status',
      render: (value) => (
        <Badge variant={value ? 'success' : 'danger'}>
          {value ? 'Active' : 'Inactive'}
        </Badge>
      )
    },
    {
      key: 'created_at',
      title: 'Added On',
      render: (value) => format(new Date(value), 'MMM dd, yyyy HH:mm')
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="action-buttons">
          <button
            className="action-btn edit"
            onClick={() => handleOpenEdit(row)}
            title="Edit"
          >
            <FiEdit2 />
          </button>
          <button
            className="action-btn delete"
            onClick={() => {
              setSelectedIp(row);
              setShowDeleteConfirm(true);
            }}
            title="Remove"
          >
            <FiTrash2 />
          </button>
        </div>
      )
    }
  ];

  return (
    <Layout title="IP Whitelist">
      <div className="ip-whitelist-page">
        <Card
          title="IP Whitelist Management"
          subtitle="Control which IP addresses can access the APIs"
          actions={
            <div className="card-actions-row">
              <select
                className="project-filter-select"
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
              >
                <option value="">All Projects</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.display_name}</option>
                ))}
              </select>
              <Button onClick={handleOpenCreate} size="small">
                <FiPlus /> Add IP
              </Button>
            </div>
          }
        >
          <Table
            columns={columns}
            data={ipList}
            loading={loading}
            emptyMessage="No IP addresses whitelisted"
          />

          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            onPageChange={(page) =>
              dispatch(fetchIpWhitelist({ project_id: projectFilter || undefined, page }))
            }
          />
        </Card>

        {/* Create/Edit Modal */}
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={modalMode === 'create' ? 'Add IP to Whitelist' : 'Edit IP Entry'}
          size="small"
        >
          <form onSubmit={handleSubmit} className="ip-form">
            {modalMode === 'create' && (
              <>
                <Select
                  label="Project"
                  name="project_id"
                  value={formData.project_id}
                  onChange={(e) =>
                    setFormData({ ...formData, project_id: e.target.value })
                  }
                  options={projectOptions}
                  required
                />

                <Input
                  label="IP Address"
                  name="ip_address"
                  value={formData.ip_address}
                  onChange={(e) =>
                    setFormData({ ...formData, ip_address: e.target.value })
                  }
                  placeholder="e.g., 192.168.1.1"
                  required
                />
              </>
            )}

            <Input
              label="Description"
              name="description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Optional description"
            />

            {modalMode === 'edit' && (
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                  />
                  <span>Active</span>
                </label>
              </div>
            )}

            <div className="form-actions">
              <Button
                variant="secondary"
                type="button"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" loading={loading}>
                {modalMode === 'create' ? 'Add IP' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </Modal>

        {/* Delete Confirmation */}
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          title="Remove IP from Whitelist"
          message={`Are you sure you want to remove ${selectedIp?.ip_address} from the whitelist?`}
          confirmText="Remove"
          loading={loading}
        />
      </div>
    </Layout>
  );
};

export default IpWhitelist;
